/**
 * Sprint 23.5 — HireDraftReportsTo.
 *
 * Single-parent select over the workspace's agent tree. Mirrors the
 * NewAgentModal pattern. Workspace root is the default ("Top-level"); other
 * agents are listed indented by depth so the hierarchy reads.
 *
 * Why parent-agent, not human manager: per
 * [[feedback-agents-are-workforce-humans-supervise]] — workforce hierarchy
 * is agent → agent. Humans supervise the whole workforce.
 */

import { useMemo, type CSSProperties } from "react";
import type { LibraryNode } from "../../../lib/types";

export interface HireDraftReportsToProps {
  value: string | null;
  rootNodeId: string | null;
  workspaceName: string;
  allNodes: LibraryNode[];
  onChange: (value: string | null) => void;
}

function buildAgentTree(nodes: LibraryNode[]): { node: LibraryNode; indent: number }[] {
  const agents = nodes.filter((n) => n.type === "agent" && !n.archived_at);
  const agentIds = new Set(agents.map((a) => a.id));
  const childMap = new Map<string, LibraryNode[]>();
  for (const a of agents) {
    const pid = a.parent_id ?? "orphan";
    if (!childMap.has(pid)) childMap.set(pid, []);
    childMap.get(pid)!.push(a);
  }
  const result: { node: LibraryNode; indent: number }[] = [];
  function walk(parentId: string, depth: number) {
    const children = childMap.get(parentId) ?? [];
    for (const child of children) {
      result.push({ node: child, indent: depth });
      walk(child.id, depth + 1);
    }
  }
  for (const a of agents) {
    if (!a.parent_id || !agentIds.has(a.parent_id)) {
      result.push({ node: a, indent: 0 });
      walk(a.id, 1);
    }
  }
  return result;
}

export function HireDraftReportsTo({
  value,
  rootNodeId,
  workspaceName,
  allNodes,
  onChange,
}: HireDraftReportsToProps) {
  const tree = useMemo(() => buildAgentTree(allNodes), [allNodes]);

  // Coerce null/missing to rootNodeId so the select always has a sensible
  // selection. The wizard treats reports_to=null as "report to workspace
  // root" — exactly what create_agent does when given no parent_id.
  const selected = value ?? rootNodeId ?? "";

  return (
    <div style={wrap}>
      <div style={labelText}>Reports to</div>
      <select
        style={select}
        value={selected}
        onChange={(e) =>
          onChange(e.target.value === rootNodeId ? null : e.target.value || null)
        }
        aria-label="Reports to"
      >
        {rootNodeId && (
          <option value={rootNodeId}>{workspaceName} (top-level)</option>
        )}
        {tree.map(({ node, indent }) => (
          <option key={node.id} value={node.id}>
            {"  ".repeat(rootNodeId ? indent + 1 : indent)}
            {node.name}
          </option>
        ))}
      </select>
    </div>
  );
}

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  paddingBottom: 10,
};

const labelText: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const select: CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "6px 8px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  cursor: "pointer",
};
